import DOMPurify from "dompurify";
import React, { FC } from "react";
import { renderToString } from "react-dom/server";
import BlockRenderer from "../BlockRenderer/BlockRenderer";
import { createPortal } from "react-dom";

const localPurifier = DOMPurify();

const cleanCSSPropertyInRecurence = (
  node: HTMLElement,
  cssProperty: string
) => {
  if (node.style?.[cssProperty]) {
    node.style.removeProperty(cssProperty);
  }
  node.childNodes.forEach((child: HTMLElement) => {
    cleanCSSPropertyInRecurence(child, cssProperty);
  });
};

const checkDataAttributeExistenceInRecurence = (
  node: HTMLElement,
  dataAttribute: string
) => {
  if (!node?.dataset) {
    return false;
  }

  if (node.dataset[dataAttribute]) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkDataAttributeExistenceInRecurence(child, dataAttribute)
      )
    : false;
};

const checkChildrenExsitenceInRecurence = (
  node: HTMLElement,
  nodeName: string
) => {
  if (
    node.childNodes.length > 0 &&
    Array.from(node.childNodes).some(
      (child: HTMLElement) => child.nodeName === nodeName
    )
  ) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkChildrenExsitenceInRecurence(child, nodeName)
      )
    : false;
};

const checkClassExistenceInRecurence = (
  node: HTMLElement,
  className: string
) => {
  if (node.classList?.contains(className)) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkClassExistenceInRecurence(child, className)
      )
    : false;
};

localPurifier.addHook("beforeSanitizeAttributes", (node: HTMLElement) => {
  if (node.style?.fontFamily) {
    node.style.removeProperty("font-family");
    node.style.removeProperty("font-size");
    node.style.removeProperty("background-color");
  }

  if (node.nodeName === "H2") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h2 md:text-article-h2 mb-8 text-primary"
    );
  }

  if (node.nodeName === "H3") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h3 md:text-article-h3 mb-8 text-primary"
    );
  }

  if (node.nodeName === "H4") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-mob-article-h4 md:text-article-h4 mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "H5") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-lg mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "H6") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto mx-3 text-base mb-8 font-medium text-primary"
    );
  }

  if (node.nodeName === "A") {
    node.style.removeProperty("color");
    node.setAttribute("class", "text-primaryBlue underline font-semibold");
  }

  if (
    node.nodeName === "IMG" ||
    checkChildrenExsitenceInRecurence(node, "IMG")
  ) {
    if (node.style?.float) {
      node.classList.remove("md:mx-auto");
      node.classList.remove("mx-3");
      node.classList.remove("max-w-[964px]");
      node.classList.remove("max-w-[836px]");
      node.parentElement?.classList.remove("max-w-[964px]");
    }

    if (node.style?.float === "left") {
      node.classList.add("mr-8");
      node.classList.add("mb-8");
    }

    if (node.style?.float === "right") {
      node.classList.add("ml-8");
      node.classList.add("mb-8");
    }

    if (
      node.classList.contains("wide-image") ||
      checkClassExistenceInRecurence(node, "wide-image")
    ) {
      node.classList.add("w-full");
    } else {
      node.classList.add("max-w-[964px]");
    }
  }

  if (
    node.nodeName === "IFRAME" ||
    checkChildrenExsitenceInRecurence(node, "IFRAME")
  ) {
    node.classList?.add("w-full");
    node.classList?.add("max-w-[1192px]");
  }

  if (node.nodeName === "BLOCKQUOTE") {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto bg-[#EBF8EE] p-14 md:text-mob-article-subtitle text-mob-article-paragraph text-center"
    );
    node.childNodes.forEach((child: HTMLElement) => {
      child.style?.removeProperty("text-align");
      cleanCSSPropertyInRecurence(child, "font-size");
    });
  }

  if (node.nodeName === "UL" || node.nodeName === "OL") {
    node.setAttribute(
      "class",
      "max-w-[836px] mx-3 md:mx-auto text-primary md:text-article-paragraph"
    );
  }

  if (node.nodeName === "UL") {
    node.classList?.add("list-disc");
  }

  if (node.nodeName === "OL") {
    node.classList?.add("list-decimal");
  }

  if (node.nodeName === "LI") {
    node.classList?.add("marker:text-primary");
    node.classList?.add("text-primary");
    node.classList?.add("md:text-article-paragraph");
    node.style.setProperty("display", "list-item");
    node.style.setProperty("margin-inline-start", "16px");
  }

  if (node.nodeName === "EM") {
    node.classList?.add("class", "italic");
  }
  if (node.nodeName === "P") {
    node.classList?.add("md:text-article-paragraph");
    node.classList?.add("text-primary");
    node.classList?.add("mb-8");
  }

  if (node.nodeName === "TABLE" || node.nodeName === "TR") {
    node.classList?.add("text-primary");
  }

  if (node.classList?.contains("img-flow")) {
    node.classList?.add("min-h-[670px]");
    node.classList?.remove("img-flow");
  }

  if (node.classList?.contains("quote")) {
    node.setAttribute(
      "class",
      "max-w-[836px] md:mx-auto bg-[#EBF8EE] p-14 md:text-mob-article-subtitle text-mob-article-paragraph text-center"
    );
    node.childNodes.forEach((child: HTMLElement) => {
      child.style?.removeProperty("text-align");
      cleanCSSPropertyInRecurence(child, "font-size");
    });
  }

  if (node.classList?.contains("vcard")) {
    node.classList?.add("max-w-[836px]");
    node.classList?.add("md:mx-auto");
    node.classList?.add("p-0");
    node.classList?.add("static");
    node.classList?.remove("max-w-[964px]");
  }

  if (node.classList?.contains("vcard__photo")) {
    node.classList?.add("static");
    node.classList?.add("float-right");
  }
});

localPurifier.addHook("afterSanitizeElements", (node: HTMLElement) => {
  node.style?.removeProperty("margin");

  if (
    node.classList?.contains("wide-image") ||
    checkClassExistenceInRecurence(node, "wide-image")
  ) {
    node.classList?.add("mx-auto");
  } else if (
    !checkDataAttributeExistenceInRecurence(node, "blockId") &&
    !checkChildrenExsitenceInRecurence(node, "IFRAME") &&
    node.nodeName !== "IFRAME"
  ) {
    node.classList?.add("max-w-[836px]");
    if (
      !node.classList?.contains("TextRun") &&
      !node.classList?.contains("NormalTextRun") &&
      !node.classList?.contains("Paragraph") &&
      node.nodeName !== "SPAN"
    ) {
      node.classList?.add("mx-3");
      node.classList?.add("md:mx-auto");
    } else {
      node.classList?.add("mx-auto");
    }
  } else {
    node.classList?.add("max-w-[1192px]");
    node.classList?.add("mx-auto");
    node.setAttribute("height", "670");
  }
});

const blockClassesOverrides = {
  TextAndImageBlock: {
    overridesForRightClasses:
      "w-full lg:w-1/2 justify-end p-8 md:p-16 flex flex-col whitespace-pre-wrap lg:pr-8",
    overridesForTextClasses: "w-full",
  },
};

interface RTEBlockProps {
  content: string;
}

const RTEBlock: FC<RTEBlockProps> = ({ content }) => {
  const htmlRef = React.useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = React.useState<HTMLElement[]>([]);

  React.useEffect(() => {
    if (!htmlRef.current) {
      return;
    }
    if (htmlRef.current) {
      const nodes: HTMLElement[] = Array.from(
        htmlRef.current.querySelectorAll("[data-block-id]")
      );
      setNodes(nodes);
    }
  }, [htmlRef.current]);

  return (
    <>
      <div
        className=" mx-auto my-12"
        ref={htmlRef}
        dangerouslySetInnerHTML={{
          __html: localPurifier.sanitize(content, {
            ALLOWED_TAGS: [
              "iframe",
              "h2",
              "h3",
              "h4",
              "h5",
              "h6",
              "a",
              "img",
              "blockquote",
              "ul",
              "li",
              "ol",
              "em",
              "p",
              "span",
              "div",
              "br",
              "table",
              "tr",
              "th",
              "td",
              "script",
            ],
            ALLOWED_ATTR: ["src", "type", "async", "defer", "charset", "class"],
          }),
        }}
      />
      {nodes.map((component) => {
        const contentId = component.getAttribute("data-block-id");
        const jsxElement = (
          <BlockRenderer
            contentId={contentId}
            overridesForClasses={blockClassesOverrides}
          />
        );

        return createPortal(jsxElement, component);
      })}
    </>
  );
};

export default RTEBlock;
